import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative w-full rounded-xl border-0 bg-card px-4 py-3.5 text-[13px] leading-5 text-foreground [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-8",
  {
    variants: {
      variant: {
        default:
          "shadow-[0_0_0_1px_rgb(9_9_11/0.08)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]",
        destructive:
          "bg-danger-tint shadow-[0_0_0_1px_#FECDCA] dark:shadow-[0_0_0_1px_rgb(240_68_56/0.35)] [&>svg]:text-danger",
        success:
          "shadow-[0_0_0_1px_rgb(9_9_11/0.08)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)] [&>svg]:text-success",
        warning:
          "bg-warning-tint shadow-[0_0_0_1px_#FEDF89] dark:shadow-[0_0_0_1px_rgb(247_144_9/0.35)] [&>svg]:text-warning",
        info:
          "bg-muted/60 shadow-[0_0_0_1px_rgb(9_9_11/0.06)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)] [&>svg]:text-foreground/60",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
))
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 text-[13px] font-semibold leading-4", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-[13px] leading-5 text-foreground/70 [&_p]:leading-5", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
